/** Smart Supplier Comparator — domain types (Sprint 1.4) */

export type ComparisonSortBy =
  | 'price_asc'
  | 'score_desc'
  | 'delivery_asc'
  | 'stock_desc'
  | 'freight_asc'

export interface RankingWeights {
  price: number
  stock: number
  delivery: number
  freight: number
  reliability: number
}

export interface ComparisonProductInput {
  id: string
  supplier: string
  supplierName?: string
  description: string
  brand: string
  model: string
  sku: string
  ean?: string
  imageUrl?: string
  category?: string
  pixPrice: number
  cardPrice: number
  purchasePrice?: number
  installments?: string
  freight?: number
  estimatedDelivery: number
  stock: number
  reliability?: number
  lastUpdate?: string
  warranty?: string
  productUrl?: string
}

export interface RankedOffer extends ComparisonProductInput {
  groupKey: string
  score: number
  scoreBreakdown: {
    price: number
    stock: number
    delivery: number
    freight: number
    reliability: number
  }
  flags: {
    bestPrice: boolean
    bestStock: boolean
    fastestDelivery: boolean
    bestScore: boolean
  }
  alerts: ComparisonAlert[]
}

export interface ProductComparisonGroup {
  groupKey: string
  label: string
  brand: string
  model: string
  sku: string
  offers: RankedOffer[]
  recommended: RankedOffer | null
  economy: number
  priceDiff: number
}

export interface ComparisonAlert {
  code: 'PRICE_TOO_HIGH' | 'OUT_OF_STOCK' | 'LONG_DELIVERY'
  severity: 'warning' | 'danger'
  message: string
}

export interface ComparisonFilters {
  suppliers?: string[]
  brands?: string[]
  categories?: string[]
  minPrice?: number
  maxPrice?: number
  inStockOnly?: boolean
  maxDeliveryDays?: number
  query?: string
}

export interface ComparisonSummary {
  totalOffers: number
  groups: number
  recommendedSupplier: string | null
  economy: number
  priceDiff: number
  bestPrice: number | null
  bestScore: number | null
}

export interface ComparisonHistoryEntry {
  id: string
  product: string
  supplierWinner: string
  economy: number
  date: string
}

export interface ComparisonStatistics {
  cheapestSupplier: string | null
  fastestSupplier: string | null
  mostUsedSupplier: string | null
  totalEconomy: number
  topSuppliers: Array<{ supplier: string; wins: number; economy: number; avgScore: number }>
  charts: {
    price: Array<{ supplier: string; value: number }>
    score: Array<{ supplier: string; value: number }>
    economy: Array<{ supplier: string; value: number }>
  }
}

export const DEFAULT_RANKING_WEIGHTS: RankingWeights = {
  price: 0.4,
  stock: 0.2,
  delivery: 0.2,
  freight: 0.1,
  reliability: 0.1,
}
